import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { 
  Search, 
  MapPin, 
  DollarSign, 
  Building, 
  Calendar,
  ArrowRight,
  Briefcase,
  Star,
  ChevronLeft,
  ChevronRight
} from 'lucide-react'
import { apiClient } from '@/lib/api'
import LocationAutocomplete from '@/components/common/LocationAutocomplete'
import CandidateHeader from '@/components/common/CandidateHeader'

interface PublicJob {
  uuid: string
  title: string
  company_name: string
  department_name?: string
  primary_location?: string
  employment_type: 'full_time' | 'part_time' | 'contract' | 'temporary' | 'internship'
  salary_min?: number
  salary_max?: number
  salary_currency?: string
  description: string
  short_description?: string
  created_at: string
  application_deadline?: string
  featured?: boolean
  experience_level: string
  remote_type: string
}


export default function JobsPage() {
  const [jobs, setJobs] = useState<PublicJob[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchText, setSearchText] = useState('')
  const [locationFilter, setLocationFilter] = useState('')
  const [typeFilter, setTypeFilter] = useState<string>('all')
  const [departmentFilter, setDepartmentFilter] = useState<string>('all')
  const [page, setPage] = useState(1)
  const pageSize = 20
  const [totalJobs, setTotalJobs] = useState(0)
  const [refreshing, setRefreshing] = useState(false)
  const initialLoadRef = useRef(true)

  const shuffleJobs = (list: PublicJob[]) => {
    const result = [...list]
    for (let i = result.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1))
      ;[result[i], result[j]] = [result[j], result[i]]
    }
    return result
  }

  useEffect(() => {
    loadJobs()
  }, [searchQuery, locationFilter, typeFilter, departmentFilter, page])


  const loadJobs = async () => {
    const isInitialLoad = initialLoadRef.current
    try {
      if (isInitialLoad) {
        setLoading(true)
      } else {
        setRefreshing(true)
      }
      // Use the new public jobs API
      const response = await apiClient.getPublicJobs({
        search: searchQuery.trim() || undefined,
        location: locationFilter.trim() || undefined,
        type: typeFilter === 'all' ? undefined : typeFilter,
        department: departmentFilter === 'all' ? undefined : departmentFilter,
        limit: pageSize,
        offset: (page - 1) * pageSize
      })
      
      if (response.success && response.data) {
        // Handle the backend response structure
        const jobsArray = response.data.jobs || response.data || []
        const publicJobs = Array.isArray(jobsArray) ? jobsArray : []
        
        // Map the API response to match frontend expectations
        const mappedJobs = publicJobs.map(job => ({
          ...job,
          company_name: job.company || job.company_name,
          primary_location: job.location || job.primary_location,
          employment_type: job.type || job.employment_type || 'full_time',
          created_at: job.datePosted || job.created_at,
          salary_min: job.salary?.min || job.salary_min,
          salary_max: job.salary?.max || job.salary_max,
          salary_currency: job.salary?.currency || job.salary_currency,
          department_name: job.department || job.department_name
        }))
        const shuffledJobs = shuffleJobs(mappedJobs)
        const limitedJobs = shuffledJobs.slice(0, 20)

        setJobs(limitedJobs)
        setTotalJobs(limitedJobs.length)
      } else if (isInitialLoad) {
        setJobs([])
        setTotalJobs(0)
      }
    } catch (error) {
      console.error('Failed to load jobs:', error)
      if (isInitialLoad) {
        setJobs([])
        setTotalJobs(0)
      }
    } finally {
      if (isInitialLoad) {
        initialLoadRef.current = false
        setLoading(false)
      }
      setRefreshing(false)
    }
  }

  const handleLocationChange = (value: string) => {
    setLocationFilter(value)
    setPage(1)
  }

  const handleSearchChange = (value: string) => {
    setSearchText(value)

    if (value.length === 0 || value.length >= 4) {
      setSearchQuery(value)
      setPage(1)
    }
  }

  const handleSearchSubmit = () => {
    if (searchText.length === 0 || searchText.length >= 4) {
      setSearchQuery(searchText)
      setPage(1)
    }
  }

  const handleTypeChange = (value: string) => {
    setTypeFilter(value)
    setPage(1)
  }

  const handleDepartmentChange = (value: string) => {
    setDepartmentFilter(value)
    setPage(1)
  }

  const handlePageChange = (newPage: number) => {
    setPage(newPage)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const formatSalary = (job: PublicJob) => {
    if (!job.salary_min || !job.salary_max) return 'Competitive salary'
    const currency = job.salary_currency === 'USD' ? '$' : job.salary_currency || '$'
    return `${currency}${(job.salary_min / 1000).toFixed(0)}k - ${currency}${(job.salary_max / 1000).toFixed(0)}k`
  }

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'full_time': return 'bg-blue-100 text-blue-800'
      case 'part_time': return 'bg-yellow-100 text-yellow-800'
      case 'contract': return 'bg-purple-100 text-purple-800'
      case 'temporary': return 'bg-orange-100 text-orange-800'
      case 'internship': return 'bg-green-100 text-green-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getRemoteTypeColor = (remoteType: string) => {
    switch (remoteType) {
      case 'remote': return 'bg-green-100 text-green-800'
      case 'hybrid': return 'bg-blue-100 text-blue-800'
      case 'on_site': return 'bg-gray-100 text-gray-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const filteredJobs = jobs || []

  const featuredJobs = filteredJobs.filter(job => job.featured)
  const regularJobs = filteredJobs.filter(job => !job.featured)

  const departments = [...new Set((jobs || []).map(job => job.department_name).filter(Boolean))]

  const totalPages = Math.max(1, Math.ceil((totalJobs || filteredJobs.length || 1) / pageSize))
  const hasNextPage = totalJobs ? (page * pageSize) < totalJobs : filteredJobs.length === pageSize
  const startIndex = (page - 1) * pageSize + 1
  const endIndex = Math.min(page * pageSize, totalJobs || filteredJobs.length)

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading amazing opportunities...</p>
        </div>
      </div>
    )
  }

  const handleCreateAlert = () => {
    console.log('Create job alert')
    // TODO: Implement job alert creation
  }

  const handleUploadResume = () => {
    console.log('Upload resume')
    // TODO: Implement resume upload
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Job Seeker Navigation Header */}
      <CandidateHeader 
        onCreateAlert={handleCreateAlert}
        onUploadResume={handleUploadResume}
      />

      {/* Page Title Section */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Find Your Dream Job</h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Discover exciting opportunities with top companies. Apply today and take the next step in your career.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-20 lg:pb-8">
        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-2">
              <div className="flex items-center space-x-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={searchText}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    placeholder="Search jobs, companies, or keywords..."
                    className="pl-10 pr-4 py-3 w-full border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                    style={{ color: '#111827', backgroundColor: '#ffffff' }}
                  />
                </div>
                <button
                  type="button"
                  onClick={handleSearchSubmit}
                  className="px-4 py-3 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
                >
                  Search
                </button>
              </div>
            </div>
            
            <div className="relative">
              <LocationAutocomplete
                value={locationFilter}
                onChange={handleLocationChange}
                placeholder="Search location..."
                className="text-gray-900"
              />
            </div>

            <select
              value={typeFilter}
              onChange={(e) => handleTypeChange(e.target.value)}
              className="px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
              style={{ color: '#111827', backgroundColor: '#ffffff' }}
            >
              <option value="all">All Types</option>
              <option value="full_time">Full Time</option>
              <option value="part_time">Part Time</option>
              <option value="contract">Contract</option>
              <option value="temporary">Temporary</option>
              <option value="internship">Internship</option>
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <select
              value={departmentFilter}
              onChange={(e) => handleDepartmentChange(e.target.value)}
              className="px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
              style={{ color: '#111827', backgroundColor: '#ffffff' }}
            >
              <option value="all">All Departments</option>
              {departments.map(dept => (
                <option key={dept} value={dept}>{dept}</option>
              ))}
            </select>
            
            <div className="flex items-center justify-end text-sm text-gray-600">
              {refreshing && (
                <div className="flex items-center text-blue-600 mr-3">
                  <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mr-2"></div>
                  Updating results...
                </div>
              )}
              <Briefcase className="w-4 h-4 mr-2" />
              {(totalJobs || filteredJobs.length)} jobs found
            </div>
          </div>
        </div>

        {/* Featured Jobs */}
        {featuredJobs.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center mb-6">
              <Star className="w-6 h-6 text-yellow-500 mr-2" />
              <h2 className="text-2xl font-bold text-gray-900">Featured Jobs</h2>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {featuredJobs.map((job) => (
                <motion.div
                  key={job.uuid}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white rounded-lg shadow-md border border-yellow-200 hover:shadow-lg transition-shadow p-6"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center mb-3">
                        <span className="bg-yellow-100 text-yellow-800 text-xs font-medium px-2 py-1 rounded-full mr-3">
                          Featured
                        </span>
                        <span className={`text-xs font-medium px-2 py-1 rounded-full ${getTypeColor(job.employment_type)}`}>
                          {(job.employment_type || 'full_time').replace('_', ' ')}
                        </span>
                      </div>
                      
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">{job.title}</h3>
                      <div className="flex items-center text-gray-600 mb-2">
                        <Building className="w-4 h-4 mr-2" />
                        <span className="font-medium">{job.company_name}</span>
                        <span className="mx-2">•</span>
                        <span>{job.department_name || 'General'}</span>
                      </div>
                      
                      <div className="flex items-center text-gray-600 mb-3">
                        <MapPin className="w-4 h-4 mr-2" />
                        <span>{job.primary_location || 'Location TBD'}</span>
                      </div>

                      {(job.salary_min && job.salary_max) && (
                        <div className="flex items-center text-green-600 mb-3">
                          <DollarSign className="w-4 h-4 mr-2" />
                          <span className="font-semibold">{formatSalary(job)}</span>
                        </div>
                      )}

                      <p className="text-gray-600 mb-4 line-clamp-3">{job.description}</p>
                      
                      <div className="flex items-center justify-between">
                        <div className="flex items-center text-sm text-gray-500">
                          <Calendar className="w-4 h-4 mr-1" />
                          Posted {job.created_at ? new Date(job.created_at).toLocaleDateString() : 'Recently'}
                        </div>
                        
                        <Link to={`/jobs/${job.uuid}`}>
                          <button className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors">
                            Apply Now
                            <ArrowRight className="w-4 h-4 ml-2" />
                          </button>
                        </Link>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* Regular Jobs */}
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">All Jobs</h2>
          <div className="space-y-4">
            {regularJobs.map((job) => (
              <motion.div
                key={job.uuid}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow p-6"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center mb-2">
                      <span className={`text-xs font-medium px-2 py-1 rounded-full ${getTypeColor(job.employment_type)} mr-3`}>
                        {(job.employment_type || 'full_time').replace('_', ' ')}
                      </span>
                    </div>
                    
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{job.title}</h3>
                    <div className="flex items-center text-gray-600 mb-2">
                      <Building className="w-4 h-4 mr-2" />
                      <span className="font-medium">{job.company_name}</span>
                      <span className="mx-2">•</span>
                      <span>{job.department_name || 'General'}</span>
                    </div>
                    
                    <div className="flex items-center text-gray-600 mb-3">
                      <MapPin className="w-4 h-4 mr-2" />
                      <span>{job.primary_location || 'Location TBD'}</span>
                      {(job.salary_min && job.salary_max) && (
                        <>
                          <span className="mx-2">•</span>
                          <DollarSign className="w-4 h-4 mr-1" />
                          <span className="text-green-600 font-semibold">{formatSalary(job)}</span>
                        </>
                      )}
                    </div>

                    <p className="text-gray-600 mb-3 line-clamp-2">{job.description}</p>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-sm text-gray-500">
                        <Calendar className="w-4 h-4 mr-1" />
                        Posted {job.created_at ? new Date(job.created_at).toLocaleDateString() : 'Recently'}
                      </div>
                      
                      <div className="flex space-x-3">
                        <Link to={`/jobs/${job.uuid}`}>
                          <button className="text-blue-600 hover:text-blue-800 text-sm font-medium transition-colors">
                            View Details
                          </button>
                        </Link>
                        <Link to={`/jobs/${job.uuid}`}>
                          <button className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors">
                            Apply
                            <ArrowRight className="w-4 h-4 ml-2" />
                          </button>
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {filteredJobs.length > 0 && (
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mt-8">
            <div className="text-sm text-gray-600 mb-3 md:mb-0">
              Showing {startIndex}-{endIndex} of {totalJobs || filteredJobs.length} jobs
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => handlePageChange(Math.max(1, page - 1))}
                disabled={page === 1}
                className={`inline-flex items-center px-3 py-2 border rounded-md text-sm font-medium ${
                  page === 1 ? 'text-gray-400 border-gray-200 cursor-not-allowed' : 'text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Previous
              </button>
              <span className="text-sm text-gray-700">Page {page} of {totalPages}</span>
              <button
                onClick={() => handlePageChange(page + 1)}
                disabled={!hasNextPage}
                className={`inline-flex items-center px-3 py-2 border rounded-md text-sm font-medium ${
                  !hasNextPage ? 'text-gray-400 border-gray-200 cursor-not-allowed' : 'text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </button>
            </div>
          </div>
        )}

        {filteredJobs.length === 0 && (
          <div className="text-center py-12">
            <Briefcase className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-medium text-gray-900 mb-2">No jobs found</h3>
            <p className="text-gray-600">Try adjusting your search criteria or check back later for new opportunities.</p>
          </div>
        )}
      </div>
    </div>
  )
}
